import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, XCircle, Clock, ArrowLeft } from 'lucide-react';

const PaiementSuccesPage = () => {
  const { t, formatCurrency } = useLanguage();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    try {
      // Stripe payment flow only
      const sessionId = searchParams.get('session_id');
      const amount = searchParams.get('amount');

      if (!sessionId) {
        setError(t('paySuccess.noPaymentToVerify'));
        setLoading(false);
        return;
      }

      const endpoint = `/payments/stripe/status/${sessionId}`;

      let attempts = 0;
      const maxAttempts = 6;
      const poll = async () => {
        attempts++;
        try {
          const res = await api.get(endpoint);
          setStatus({ ...res.data, display_amount: amount });

          if (res.data.status === 'COMPLETED' || attempts >= maxAttempts) {
            setLoading(false);
            return;
          }
          setTimeout(poll, 2000);
        } catch (err) {
          if (attempts >= maxAttempts) {
            setError(t('paySuccess.statusCheckFailed'));
            setLoading(false);
          } else {
            setTimeout(poll, 2000);
          }
        }
      };
      await poll();
    } catch (err) {
      setError(err.response?.data?.detail || t('paySuccess.verificationError'));
      setLoading(false);
    }
  };

  const goBack = () => {
    if (user?.role === 'client') {
      navigate('/espace-client');
    } else {
      navigate('/dashboard');
    }
  };

  const getStatusDisplay = () => {
    if (!status) return null;
    const s = (status.status || '').toUpperCase();

    if (s === 'COMPLETED') {
      return {
        icon: <CheckCircle className="w-16 h-16 text-green-400" />,
        title: t('paySuccess.completedTitle'),
        description: t('paySuccess.completedDesc'),
        color: 'text-green-400',
        bgColor: 'border-green-500/30 bg-green-500/5'
      };
    }

    if (s === 'FAILED' || s === 'CANCELLED' || s === 'UNPAID') {
      return {
        icon: <XCircle className="w-16 h-16 text-red-400" />,
        title: t('paySuccess.failedTitle'),
        description: t('paySuccess.failedDesc'),
        color: 'text-red-400',
        bgColor: 'border-red-500/30 bg-red-500/5'
      };
    }

    return {
      icon: <Clock className="w-16 h-16 text-yellow-400" />,
      title: t('paySuccess.pendingTitle'),
      description: t('paySuccess.pendingDesc'),
      color: 'text-yellow-400',
      bgColor: 'border-yellow-500/30 bg-yellow-500/5'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="payment-success-loading">
        <div className="grid-pattern"></div>
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-400">{t('paySuccess.verifying')}</p>
        </div>
      </div>
    );
  }

  const display = getStatusDisplay();

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="grid-pattern"></div>
      <div className={`w-full max-w-md rounded-2xl border ${display?.bgColor || 'border-white/10 bg-white/5'} p-8 text-center`} data-testid="payment-success-card">

        {error ? (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-400 mb-2" data-testid="payment-success-error-title">{t('paySuccess.errorTitle')}</h2>
            <p className="text-gray-300 text-sm mb-6" data-testid="payment-success-error">{error}</p>
          </>
        ) : display ? (
          <>
            <div className="flex justify-center mb-4" data-testid="payment-success-icon">
              {display.icon}
            </div>
            <h2 className={`text-2xl font-bold ${display.color} mb-2`} data-testid="payment-success-title">
              {display.title}
            </h2>
            <p className="text-gray-300 text-sm mb-4" data-testid="payment-success-description">
              {display.description}
            </p>

            {status && (
              <div className="space-y-2 text-sm text-gray-400 mb-6 text-left p-4 rounded-xl bg-black/30" data-testid="payment-success-details">
                {status.display_amount && (
                  <p>{t('paySuccess.amountLabel')} <span className="text-green-400 font-bold">{formatCurrency(parseFloat(status.display_amount))}</span></p>
                )}
                {status.order_type === 'repair' && status.device_ref && (
                  <p>{t('paySuccess.repairLabel')} <span className="text-cyan-400 font-semibold">{status.device_ref}</span></p>
                )}
                {status.order_type === 'boutique' && status.order_id && (
                  <p>{t('paySuccess.orderLabel')} <span className="text-cyan-400 font-semibold">{status.order_id.substring(0, 8)}...</span></p>
                )}
                {status.session_id && (
                  <p>{t('paySuccess.referenceLabel')} <span className="text-gray-300">{status.session_id.substring(0, 20)}...</span></p>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-300 text-sm mb-6" data-testid="payment-success-none">{t('paySuccess.noInfo')}</p>
        )}

        <button
          onClick={goBack}
          className="w-full py-3 rounded-xl bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 font-bold flex items-center justify-center gap-2 transition-all"
          data-testid="payment-success-back"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('paySuccess.backToSpace')}
        </button>
      </div>
    </div>
  );
};

export default PaiementSuccesPage;
